import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';
import { OperationApprove } from 'src/app/shared/interfaces/operation-approve';
import { OperationApproveStatus } from 'src/app/shared/interfaces/operation-approve-status.enum';
import { OperationFilter } from '../shared/operation-filter';
import { countActiveConfirmationFilters } from '../shared/operation-filter.utils';

import { OperationTransferenceService } from './../../../services/operation/operation-transference.service';
import { OperationConfirmationTableComponent } from './operation-confirmation-table/operation-confirmation-table.component';
import { ClickEnumConfirmation, EmitClickConfirmation } from './shared/emit-click.interface';
import { OperationConfirmationService } from './shared/operation-confirmation.service';
import { getCarbonCreditCodeValue } from './shared/operation-control-number.utils';

interface OperationConfirmationToastState {
  open: boolean;
  variant: 'success' | 'danger';
  label: string;
  description: string;
}

@Component({
  standalone: false,
  selector: 'app-operation-confirmation',
  templateUrl: './operation-confirmation.component.html',
  styleUrls: ['./operation-confirmation.component.scss']
})
export class OperationConfirmationComponent implements OnInit, OnDestroy {

  @ViewChild('operationConfirmationTable') confirmationTable?: OperationConfirmationTableComponent;

  private readonly destroy$ = new Subject<void>();

  showFilter = false;
  activeFiltersCount = 0;
  showFilterUpdateNotice = false;
  isApproveModalOpen = false;
  isRejectModalOpen = false;
  isDetailsDrawerOpen = false;
  selectedOperationForApprove: CarbonOperation | null = null;
  selectedOperationForReject: CarbonOperation | null = null;
  selectedOperationForDetails: CarbonOperation | null = null;
  activeToast: OperationConfirmationToastState | null = null;

  constructor(
    private operationService: OperationTransferenceService,
    private confirmationService: OperationConfirmationService,
  ) {}

  ngOnInit(): void {
    this.confirmationService.filters
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        this.activeFiltersCount = countActiveConfirmationFilters(filters);
        this.showFilterUpdateNotice = this.activeFiltersCount > 0;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeFiltersLabel(): string {
    return this.activeFiltersCount === 1
      ? '1 filtro realizado'
      : `${this.activeFiltersCount} filtros realizados`;
  }

  get filterButtonAriaLabel(): string {
    return `Filtrar, ${this.activeFiltersLabel}`;
  }

  handleTableAction(event: EmitClickConfirmation): void {
    if(event.row && event.type) {
      switch (event.type) {
        case ClickEnumConfirmation.APROVAR:
          this.openApproveModal(event.row);
        break;
        case ClickEnumConfirmation.DETALHES:
          this.openOperationDetail(event.row);
        break;
        case ClickEnumConfirmation.REJEITAR:
          this.openRejectModal(event.row);
        break;
        default:
          break;
      }
    }
  }

  openOperationDetail(operation: CarbonOperation): void {
    this.selectedOperationForDetails = operation;
    this.isDetailsDrawerOpen = true;
  }

  onDetailsDrawerClosed(): void {
    this.isDetailsDrawerOpen = false;
    this.selectedOperationForDetails = null;
  }

  openApproveModal(operation: CarbonOperation): void {
    this.selectedOperationForApprove = operation;
    this.isApproveModalOpen = true;
  }

  onApproveModalClosed(confirmed: boolean): void {
    this.isApproveModalOpen = false;

    if (!confirmed || !this.selectedOperationForApprove) {
      this.selectedOperationForApprove = null;
      return;
    }

    const operation = this.selectedOperationForApprove;
    this.selectedOperationForApprove = null;

    const operationApproval: OperationApprove = {
      operationCode: operation.operationCode,
      statusCode: OperationApproveStatus.APPROVED,
    };

    this.operationService.approveOperation(operationApproval).subscribe(() => {
      this.showOperationToast(operation, 'approve');
      this.confirmationTable?.updateOperationStatus(operation.operationCode, 'Finalizada');
    });
  }

  openRejectModal(operation: CarbonOperation): void {
    this.selectedOperationForReject = operation;
    this.isRejectModalOpen = true;
  }

  onRejectModalClosed(confirmed: boolean): void {
    this.isRejectModalOpen = false;

    if (!confirmed || !this.selectedOperationForReject) {
      this.selectedOperationForReject = null;
      return;
    }

    const operation = this.selectedOperationForReject;
    this.selectedOperationForReject = null;

    const operationApproval: OperationApprove = {
      operationCode: operation.operationCode,
      statusCode: OperationApproveStatus.REJECTED,
    };

    this.operationService.approveOperation(operationApproval).subscribe(() => {
      this.showOperationToast(operation, 'reject');
      this.confirmationTable?.updateOperationStatus(operation.operationCode, 'Rejeitada');
    });
  }

  onToastDismiss(): void {
    this.activeToast = null;
  }

  openFilter(): void {
    this.showFilter = true;
  }

  onFilterNoticeDismiss(): void {
    this.showFilterUpdateNotice = false;
  }

  onFilterNoticeUndo(): void {
    this.confirmationService.filters.next({} as OperationFilter);
    this.showFilterUpdateNotice = false;
  }

  private showOperationToast(operation: CarbonOperation, action: 'approve' | 'reject'): void {
    this.activeToast = {
      open: true,
      variant: action === 'approve' ? 'success' : 'danger',
      label: action === 'approve'
        ? 'Créditos de Carbono aprovado com sucesso!'
        : 'Créditos de Carbono rejeitado!',
      description: this.buildOperationToastDescription(operation),
    };
  }

  private buildOperationToastDescription(operation: CarbonOperation): string {
    const carbonCode = getCarbonCreditCodeValue(operation.carbonCreditCode) || '-';
    const registerCode = operation.registerCode?.trim() || '-';

    return `Cód. Carbono ${carbonCode} Cód. Conta Registrador ${registerCode}`;
  }
}
